<template>
    <transition name="fade">
        <div
            v-if="!dismissed"
            class="flex flex-col p-6 items-center justify-center gap-2 rounded-lg bg-white dark:bg-slate-800 shadow dark:shadow-slate-900/50 overflow-hidden mt-6"
        >
            <LoadingInline v-if="isLoading" :loading="isLoading" />
            <div
                v-if="!isLoading"
                class="flex w-full justify-between items-center"
            >
                <div class="flex items-center">
                    <icon icon="flame" />
                    <span
                        class="text-base lg:text-lg font-medium text-slate-800 dark:text-slate-100 ml-2"
                    >
                        Get started with My10x!
                    </span>
                </div>
                <span class="text-slate-800 dark:text-slate-100 font-medium text-xs lg:text-sm">
                    {{ progress }}% completed
                </span>
            </div>
            <div
                v-if="!isLoading"
                class="flex w-full py-6 pb-2 flex-col items-start gap-4"
            >
                <div
                    class="flex items-center gap-3 self-stretch border-b-2 border-gray-100 dark:border-slate-700 py-4"
                >
                    <div
                        :class="{
                            'bg-emerald-50': open > 0 || closed > 0,
                            'bg-orange-50': open === 0 && closed === 0,
                        }"
                        class="flex w-12 h-12 p-3 justify-center items-center rounded-full"
                    >
                        <icon
                            :icon="
                                open > 0 || closed > 0
                                    ? 'check-green'
                                    : 'credit-card'
                            "
                            :class="{
                                'text-green-500': open > 0 || closed > 0,
                                'text-orange': open === 0 && closed === 0,
                            }"
                        />
                    </div>
                    <div class="flex-1 min-w-0">
                        <h2 class="text-base text-slate-800 dark:text-slate-100 font-medium">
                            Add your credit cards
                        </h2>
                        <p
                            class="text-sm font-normal text-slate-500 prose max-w-2xl"
                        >
                            Add your cards to stay organized and unlock
                            personalized insights tailored to your wallet.
                        </p>
                    </div>
                    <Link
                        v-if="!isLoading && open === 0 && closed === 0"
                        href="/wallet"
                        class="btn-base btn-10x-orange flex p-2 px-4 justify-center items-center gap-2 rounded-md shadow-md text-white ml-auto text-xs lg:text-sm"
                        id="hs-dashboard-add-cards-start"
                        prefetch
                    >
                        Start
                    </Link>
                </div>

                <div
                    class="flex items-center gap-3 self-stretch border-b-2 border-gray-100 dark:border-slate-700 py-4"
                >
                    <div
                        :class="{
                            'bg-emerald-50': points > 0,
                            'bg-orange-50': points === 0,
                        }"
                        class="flex w-12 h-12 p-3 justify-center items-center rounded-full"
                    >
                        <icon
                            :icon="points > 0 ? 'check-green' : 'points'"
                            :class="{
                                'text-green-500': points > 0,
                                'text-orange': points === 0,
                            }"
                        />
                    </div>
                    <div class="flex-1 min-w-0">
                        <h2 class="text-base text-slate-800 dark:text-slate-100 font-medium">
                            Start tracking your points
                        </h2>
                        <p
                            class="text-sm font-normal text-slate-500 prose max-w-2xl"
                        >
                            Sync loyalty accounts or add balances manually to
                            track your points and miles in one place.
                        </p>
                    </div>
                    <Link
                        v-if="!isLoading && points === 0"
                        href="/mypoints"
                        class="btn-base btn-10x-orange flex p-2 px-4 justify-center items-center gap-2 rounded-md shadow-md text-white ml-auto text-xs lg:text-sm"
                        id="hs-dashboard-points-tracking-start"
                        prefetch
                    >
                        Start
                    </Link>
                </div>

                <div
                    class="flex items-center gap-3 self-stretch border-b-2 border-gray-100 dark:border-slate-700 py-4"
                >
                    <div
                        :class="{
                            'bg-emerald-50': creditScore > 0,
                            'bg-orange-50': creditScore === 0,
                        }"
                        class="flex w-12 h-12 p-3 justify-center items-center rounded-full"
                    >
                        <icon
                            :icon="
                                creditScore > 0 ? 'check-green' : 'credit-score'
                            "
                            :class="{
                                'text-green-500': creditScore > 0,
                                'text-orange': creditScore === 0,
                            }"
                        />
                    </div>
                    <div class="flex-1 min-w-0">
                        <h2 class="text-base text-slate-800 dark:text-slate-100 font-medium">
                            Start tracking your credit
                        </h2>
                        <p class="text-sm text-slate-500 dark:text-slate-400 prose max-w-2xl">
                            Monitor your credit to track changes over time and
                            make smarter rewards decisions.
                        </p>
                    </div>
                    <button
                        type="button"
                        v-if="!isLoading && creditScore === 0"
                        @click="openCreditScoreModal"
                        class="btn-base btn-10x-orange flex p-2 px-4 justify-center items-center gap-2 rounded-md shadow-md text-white ml-auto text-xs lg:text-sm"
                        id="hs-dashboard-credit-tracking-start"
                    >
                        Start
                    </button>
                </div>

                <div class="flex items-center gap-3 self-stretch py-2">
                    <div
                        :class="{
                            'bg-emerald-50': hasStartedCourse,
                            'bg-orange-50': !hasStartedCourse,
                        }"
                        class="flex w-12 h-12 p-3 justify-center items-center rounded-full"
                    >
                        <icon
                            :icon="hasStartedCourse ? 'check-green' : 'courses'"
                            :class="{
                                'text-green-500': hasStartedCourse,
                                'text-orange': !hasStartedCourse,
                            }"
                        />
                    </div>
                    <div class="flex flex-col items-start gap-1 flex-1 min-w-0">
                        <div class="text-base text-slate-800 dark:text-slate-100 font-medium">
                            Complete our course
                        </div>
                        <div
                            class="text-sm font-normal text-slate-500 prose max-w-2xl"
                        >
                            Learn the fundamentals to confidently maximize your
                            rewards and achieve your travel goals.
                        </div>
                    </div>
                    <Link
                        v-if="!isLoading && !hasStartedCourse"
                        href="/course/takeoff"
                        class="btn-base btn-10x-orange flex p-2 px-4 justify-center items-center gap-2 rounded-md shadow-md text-white ml-auto text-xs lg:text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0"
                        id="hs-dashboard-course-start"
                        prefetch
                    >
                        Start
                    </Link>
                </div>
                <button
                    v-if="progress === 100"
                    :disabled="loading"
                    class="btn-base btn-hollow bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-md font-medium text-sm py-2.5 px-4 rounded border border-slate-200 dark:border-slate-600 ml-auto focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0"
                    @click="handleDismissAction"
                    id="hs-dashboard-guide-dismiss"
                >
                    Done, hide this
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import { defineComponent } from 'vue';
import { mapState, mapActions } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';
import { useCreditScoreStore } from '@/store/creditscore';
import { useCoursesStore } from '@/store/courses';
import Icon from '../../ui/icon/icon.vue';
import LoadingInline from '@/components/ui/loading-inline.vue';
import CreditScoreModal from '@/components/creditscore/modals/credit-score.vue';

export default defineComponent({
    components: { LoadingInline, Icon, CreditScoreModal, Link },
    data() {
        return {
            isVisible: true,
        };
    },
    computed: {
        ...mapState(useCoursesStore, {
            courseProgress: (state) => state.progress,
            loadingCourse: (state) => state.loadingCourse,
        }),
        ...mapState(useDashboardStore, [
            'user',
            'open',
            'closed',
            'points',
            'loading',
        ]),
        ...mapState(useCreditScoreStore, [
            'userScoreData',
            'loadingUserCreditScore',
            'showCreditScoreModal',
        ]),
        isLoading() {
            return this.loadingCourse || this.loadingUserCreditScore;
        },
        creditScore() {
            return this.userScoreData?.latest_score || 0;
        },
        progress() {
            let progress = 0;
            if (this.open > 0 || this.closed > 0) progress += 1;
            if (this.points > 0) progress += 1;
            if (this.creditScore > 0) progress += 1;
            if (this.hasStartedCourse) progress += 1;
            return (progress / 4) * 100;
        },
        hasStartedCourse() {
            return (this.courseProgress || 0) > 0;
        },
        dismissed() {
            return this.user?.start_guide_completed;
        },
    },
    methods: {
        ...mapActions(useCoursesStore, ['getFirstCourse']),
        ...mapActions(useDashboardStore, ['updateUserPreference']),
        ...mapActions(useCreditScoreStore, ['openCreditScoreModal']),
        handleDismissAction() {
            this.dismissAction();
        },
        dismissAction() {
            this.updateUserPreference('start_guide_completed_at');
        },
    },
    mounted() {
        // Wait for next tick to ensure auth state is established, then check if user is loaded
        // This prevents 401 errors during page load race conditions (SUPPORT-208)
        this.$nextTick(() => {
            if (this.user) {
                this.getFirstCourse().catch(() => {
                    // Handle gracefully without throwing to Sentry
                    // The courses store already handles the error state
                });
            }
        });
    },
});
</script>

<style>
@reference "../../../../css/app.css";
.fade-enter-active,
.fade-leave-active {
    @apply transition-opacity duration-500;
}
.fade-enter, .fade-leave-to /* .fade-leave-active en <2.1.8 */ {
    @apply opacity-0;
}
</style>
